import { format, isBefore, parseISO, startOfDay, subDays } from 'date-fns';
import { AgentCenterResponse } from 'services/api/AgentCenterAPI';

export type FormatedCallHistoryItem = {
    callId: string;
    contactName: string;
    phoneNumber: string;
    timeOfCall: string;
    duration: string;
    actionTaken: string;
    callDate: string;
    callTime: string;
    name: string;
};

export const formatCallHistory = ({
    agentCenterData,
    dateFilter,
}: {
    agentCenterData?: AgentCenterResponse;
    dateFilter: string;
}): FormatedCallHistoryItem[] => {
    if (!agentCenterData || !agentCenterData.callHistory) return [];

    const now = new Date();
    const todayStart = startOfDay(now);
    const yesterdayStart = subDays(todayStart, 1);
    const last7DaysStart = subDays(todayStart, 7);

    const filteredCalls = agentCenterData.callHistory.filter((call) => {
        const callDate = parseISO(call.callTime);

        if (dateFilter === 'today') {
            return !isBefore(callDate, todayStart);
        }
        if (dateFilter === 'yesterday') {
            return !isBefore(callDate, yesterdayStart) && isBefore(callDate, todayStart);
        }
        if (dateFilter === 'last7days') {
            return !isBefore(callDate, last7DaysStart);
        }

        return true;
    });

    return filteredCalls.map((call) => {
        const callDateTime = parseISO(call.callTime);
        const timeOfCall = format(callDateTime, 'p');
        const callDateFormatted = format(callDateTime, 'P');

        return {
            callId: call.callId,
            contactName: call.contactName,
            phoneNumber: call.phoneNumber,
            timeOfCall,
            duration: `${call.duration} min`,
            actionTaken: call.actionTaken,
            callDate: callDateFormatted,
            callTime: timeOfCall,
            name: call.contactName,
        };
    });
};

// Format the metrics for display from agentCenterData
export const formatMetrics = ({ agentCenterData }: { agentCenterData?: AgentCenterResponse }) => {
    if (!agentCenterData) return [];

    const cs = agentCenterData.callSnapshot;
    // Format hours active from minutes
    const hoursActive = Math.floor(cs.hoursActive / 60);
    const minutesActive = cs.hoursActive % 60;
    const hoursActiveFormatted = `${hoursActive}:${minutesActive < 10 ? '0' + minutesActive : minutesActive}`;

    // Format average wait time from seconds
    const avgWaitMin = Math.floor(cs.avgWaitTime / 60);
    const avgWaitSec = cs.avgWaitTime % 60;
    const avgWaitFormatted = `${avgWaitMin}:${avgWaitSec < 10 ? '0' + avgWaitSec : avgWaitSec}`;

    return [
        { title: 'Calls Taken', value: cs.callsTaken.toString() },
        { title: 'Deals Won', value: cs.dealsWon.toString() },
        { title: 'Closing Rate', value: `${cs.closingRate}%` },
        { title: 'Hours Active', value: hoursActiveFormatted },
        { title: 'Avg. Wait Time', value: avgWaitFormatted, highlight: true },
    ];
};
